/**
 * ApiEvidenceView — renders one ``api_evidence`` artifact inside the shared
 * ``ArtifactLightbox`` body slot.
 *
 * Two layers of detail:
 *   1. Summary card — already lifted onto the artifact record by the
 *      ``tools/artifacts.py:_read_api_evidence_summary`` helper (outcome,
 *      outcome_status, category, duration_ms, resource IDs). Renders
 *      immediately, no network round-trip.
 *   2. Full request / response / error JSON — fetched once per URL via a
 *      module-level promise cache so arrow-keying prev/next/prev/next does
 *      not re-download. Rendered via the project's idiomatic
 *      ``FormattedJsonView`` (no third-party syntax-highlighter dependency).
 */
import { useEffect, useState } from "react";
import { fetchStoredFileJson } from "~/common/files/storedFileFetcher";
import { StoredFileLink } from "~/common/files/StoredFileLink";
import FormattedJsonView from "./FormattedJsonView";
import type { WorkflowArtifact } from "./WorkflowArtifactsSection";

const JSON_CACHE = new Map<string, Promise<unknown>>();

function artifactFilename(artifact: WorkflowArtifact): string | null {
  if (artifact.filename?.trim()) return artifact.filename.trim();
  try {
    const name = new URL(artifact.url, window.location.origin).pathname
      .split("/")
      .pop();
    return name || null;
  } catch {
    return null;
  }
}

function fetchPayloadViaApi(
  instanceId: string,
  filename: string,
): Promise<unknown> {
  const cacheKey = `api:${instanceId}:${filename}`;
  let promise = JSON_CACHE.get(cacheKey);
  if (!promise) {
    promise = fetch(
      `/api/workflows/instances/${encodeURIComponent(instanceId)}/artifact-payload?filename=${encodeURIComponent(filename)}`,
      { credentials: "include" },
    ).then(async (r) => {
      if (!r.ok) {
        const err = new Error(`HTTP ${r.status}`) as Error & { status?: number };
        err.status = r.status;
        throw err;
      }
      const data = (await r.json()) as { payload?: unknown };
      return data.payload ?? data;
    });
    JSON_CACHE.set(cacheKey, promise);
    promise.catch(() => JSON_CACHE.delete(cacheKey));
  }
  return promise;
}

function fetchJson(url: string): Promise<unknown> {
  let promise = JSON_CACHE.get(url);
  if (!promise) {
    promise = fetchStoredFileJson(url);
    JSON_CACHE.set(url, promise);
    // Evict on failure so a retry gets a real fetch.
    promise.catch(() => JSON_CACHE.delete(url));
  }
  return promise;
}

interface EvidencePayload {
  request?: unknown;
  response?: unknown;
  error?: unknown;
  [k: string]: unknown;
}

function StatusPill({ status }: { status: string | null | undefined }) {
  if (!status) return null;
  const ok = status === "ok";
  return (
    <span
      className={
        ok
          ? "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-primary-success-50 text-primary-success-700"
          : "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-primary-error-50 text-primary-error-700"
      }
      data-testid="api-evidence-status-pill"
    >
      <span aria-hidden="true" className="mr-1">
        {ok ? "✓" : "✗"}
      </span>
      {ok ? "Success" : status === "error" ? "Failed" : status}
    </span>
  );
}

function CategoryBadge({ category }: { category: string | null | undefined }) {
  if (!category) return null;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-gray-light-100 text-primary-gray-light-700 uppercase tracking-wide"
      data-testid="api-evidence-category-badge"
    >
      {category}
    </span>
  );
}

function ResourceIdChip({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  if (!value) return null;
  return (
    <span className="inline-flex items-center text-2xs font-mono bg-primary-gray-light-50 border border-primary-gray-light-200 rounded px-1.5 py-0.5">
      <span className="text-primary-gray-light-500 mr-1">{label}:</span>
      <span className="text-primary-gray-light-900">{value}</span>
    </span>
  );
}

export function ApiEvidenceView({
  artifact,
  instanceId,
  fetchArtifactPayload,
}: {
  artifact: WorkflowArtifact;
  instanceId?: string | null;
  fetchArtifactPayload?: (
    instanceId: string,
    filename: string,
  ) => Promise<unknown>;
}) {
  // Prefer the inline payload embedded by the substrate
  // (``tools/artifacts.py:_read_api_evidence_summary``) — it ships with
  // the artifact record, so no separate read is needed. Falls back to
  // reading the file through the signed-in /api/files route (story 25240)
  // only when the substrate omitted the payload (e.g. file above the
  // embedding size cap).
  const inlinePayload =
    artifact.payload && typeof artifact.payload === "object"
      ? (artifact.payload as EvidencePayload)
      : null;

  const [payload, setPayload] = useState<EvidencePayload | null>(inlinePayload);
  const [loading, setLoading] = useState(inlinePayload === null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Inline payload available — no network needed.
    if (inlinePayload !== null) {
      setPayload(inlinePayload);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPayload(null);
    const filename = artifactFilename(artifact);
    const loadPromise =
      instanceId && filename
        ? (fetchArtifactPayload?.(instanceId, filename) ??
          fetchPayloadViaApi(instanceId, filename))
        : fetchJson(artifact.url);
    loadPromise
      .then((data) => {
        if (cancelled) return;
        setPayload(data as EvidencePayload);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Couldn't load this API call record.");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [artifact, instanceId, fetchArtifactPayload, inlinePayload]);

  const {
    outcome,
    outcome_status,
    category,
    duration_ms,
    epic_attachment_id,
    epic_note_id,
    graph_message_id,
    graph_attachment_id,
    filename,
  } = artifact;

  // Pull request / response / error off the full JSON when available. Other
  // top-level fields (name, ts, redacted markers, etc.) get rolled into a
  // "Details" section so nothing in the JSON is silently dropped.
  const request = payload?.request;
  const response = payload?.response;
  const errorBody = payload?.error;
  const otherDetails = payload
    ? Object.fromEntries(
        Object.entries(payload).filter(
          ([k]) => k !== "request" && k !== "response" && k !== "error",
        ),
      )
    : null;

  return (
    <div
      className="flex flex-col gap-3 w-full h-full overflow-auto p-1"
      data-testid="api-evidence-view"
    >
      {/* Summary card — always visible, derived from the artifact-list record. */}
      <div className="rounded-md border border-primary-gray-light-200 bg-base-white p-3 flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill status={outcome_status} />
          <CategoryBadge category={category} />
          {typeof duration_ms === "number" && (
            <span className="text-xs text-primary-gray-light-500">
              {duration_ms} ms
            </span>
          )}
        </div>
        {outcome && (
          <p
            className="text-sm font-medium text-primary-gray-light-900"
            data-testid="api-evidence-outcome"
          >
            {outcome}
          </p>
        )}
        {(epic_attachment_id ||
          epic_note_id ||
          graph_message_id ||
          graph_attachment_id) && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            <ResourceIdChip
              label="epic_attachment_id"
              value={epic_attachment_id}
            />
            <ResourceIdChip label="epic_note_id" value={epic_note_id} />
            <ResourceIdChip
              label="graph_message_id"
              value={graph_message_id}
            />
            <ResourceIdChip
              label="graph_attachment_id"
              value={graph_attachment_id}
            />
          </div>
        )}
      </div>

      {/* Full payload — fetched on mount. */}
      {loading && (
        <div
          className="flex flex-col gap-2"
          data-testid="api-evidence-loading"
          aria-label="Loading API evidence"
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-16 rounded-md bg-primary-gray-light-100 animate-pulse"
            />
          ))}
        </div>
      )}

      {error && !loading && (
        <div
          className="rounded-md border border-primary-error-200 bg-primary-error-50 p-3 text-sm text-primary-error-800 flex items-start justify-between gap-3"
          data-testid="api-evidence-error"
        >
          <span>{error}</span>
          <StoredFileLink
            url={artifact.url}
            filename={filename}
            className="text-sunrise underline whitespace-nowrap"
          >
            Download JSON
          </StoredFileLink>
        </div>
      )}

      {!loading && !error && payload && (
        <>
          {request !== undefined && (
            <FormattedJsonView
              data={request}
              rootLabel="Request"
              testId="api-evidence-request"
            />
          )}
          {response !== undefined && (
            <FormattedJsonView
              data={response}
              rootLabel="Response"
              testId="api-evidence-response"
            />
          )}
          {errorBody !== undefined && errorBody !== null && (
            <FormattedJsonView
              data={errorBody}
              rootLabel="Error"
              testId="api-evidence-error-body"
            />
          )}
          {otherDetails && Object.keys(otherDetails).length > 0 && (
            <FormattedJsonView
              data={otherDetails}
              rootLabel="Details"
              testId="api-evidence-details"
            />
          )}
        </>
      )}
    </div>
  );
}

export default ApiEvidenceView;
